import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Groups as TeamsIcon,
  DirectionsRun as RunsIcon,
  Flag as GoalsIcon,
  EmojiEvents as PointsIcon,
  Redeem as PrizesIcon,
  Schedule as ActivityIcon,
  Logout as LogoutIcon,
  Assignment as ChallengesIcon,
  Person as ProfileIcon,
  ShoppingBag as ShopIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import NotificationIndicator from './NotificationIndicator';

interface NavigationProps {
  currentPage: string;
  onPageChange: (page: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({ currentPage, onPageChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const { user, signOut } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOutClick = () => {
    handleMenuClose();
    setMobileDrawerOpen(false);
    setLogoutConfirmOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await signOut();
      setLogoutConfirmOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleLogoutCancel = () => {
    setLogoutConfirmOpen(false);
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handlePageChange = (page: string) => {
    onPageChange(page);
    setMobileDrawerOpen(false);
  };

  // Main navigation items (shown as buttons)
  const mainNavItems = (user?.role === 'coach' || user?.role === 'headCoach')
    ? [
        { id: 'activities', label: 'Activity Dashboard', icon: <ActivityIcon /> },
        { id: 'teams', label: 'Team Management', icon: <TeamsIcon /> },
        { id: 'points', label: 'Leaderboard', icon: <PointsIcon /> },
      ]
    : [
        { id: 'activities', label: 'Activity Dashboard', icon: <ActivityIcon /> },
      ];

  // Profile menu items (shown in dropdown)
  const profileMenuItems = (user?.role === 'coach' || user?.role === 'headCoach')
    ? [
        { id: 'profile', label: 'Profile', icon: <ProfileIcon /> },
      ]
    : [
        { id: 'profile', label: 'Profile', icon: <ProfileIcon /> },
        { id: 'activities', label: 'Activity Dashboard', icon: <ActivityIcon /> },
        { id: 'points', label: 'Leaderboard', icon: <PointsIcon /> },
        // { id: 'challenges', label: 'Challenges & Missions', icon: <ChallengesIcon /> }, // Hidden for now
        { id: 'teams', label: 'Team Roster', icon: <TeamsIcon /> },
        // { id: 'shop', label: 'Avatar Shop', icon: <ShopIcon /> }, // Hidden for now
        { id: 'prizes', label: 'The Shop', icon: <PrizesIcon /> },
        // { id: 'goals', label: 'My Goals', icon: <GoalsIcon /> }, // Hidden for now
      ];

  // All navigation items for mobile drawer (filter out duplicates)
  // Get all unique item IDs from mainNavItems
  const mainNavIds = new Set(mainNavItems.map(item => item.id));
  // Filter profileMenuItems to exclude items already in mainNavItems
  const uniqueProfileItems = profileMenuItems.filter(item => !mainNavIds.has(item.id));
  const allNavigationItems = [...mainNavItems, ...uniqueProfileItems];

  const drawer = (
    <Box sx={{ width: 250, height: '100%' }}>
      <Toolbar />
      <List sx={{ pt: 1 }}>
        {allNavigationItems.map((item) => (
          <ListItem
            key={item.id}
            onClick={() => handlePageChange(item.id)}
            sx={{ 
              cursor: 'pointer',
              backgroundColor: currentPage === item.id ? 'action.selected' : 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              borderRadius: 1,
              mx: 1,
              mb: 0.5
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        <ListItem
          onClick={handleSignOutClick}
          sx={{ 
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
            mt: 2,
            borderTop: 1,
            borderColor: 'divider',
            pt: 2
          }}
        >
          <ListItemIcon><LogoutIcon /></ListItemIcon>
          <ListItemText primary="Sign Out" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed"
        sx={{
          // Position AppBar well below the status bar/system UI with comfortable spacing
          // Increased spacing to ensure notification bell and all content is below system UI
          top: {
            xs: 'calc(env(safe-area-inset-top, 32px) + 20px)', // Safe area + 20px extra for comfortable spacing
            sm: 'calc(env(safe-area-inset-top, 32px) + 20px)',
          },
          // Add extra padding for comfortable spacing
          paddingTop: {
            xs: '8px', // Additional padding inside AppBar
            sm: '8px',
          },
        }}
      >
        <Toolbar
          sx={{
            // Ensure toolbar content is properly spaced
            minHeight: {
              xs: '56px !important', // Standard Material-UI toolbar height
              sm: '64px !important',
            },
          }}
        >
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleMobileDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Streaker
          </Typography>

          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {mainNavItems.map((item) => (
                <Button
                  key={item.id}
                  color="inherit"
                  onClick={() => handlePageChange(item.id)}
                  sx={{
                    backgroundColor: currentPage === item.id ? 'rgba(255,255,255,0.1)' : 'transparent',
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <NotificationIndicator onPageChange={onPageChange} />
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {profileMenuItems.map((item) => (
                <MenuItem 
                  key={item.id}
                  onClick={() => {
                    handlePageChange(item.id);
                    handleMenuClose();
                  }}
                  sx={{
                    backgroundColor: currentPage === item.id ? 'action.selected' : 'transparent',
                  }}
                >
                  <ListItemIcon>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText>{item.label}</ListItemText>
                </MenuItem>
              ))}
              <MenuItem 
                onClick={handleSignOutClick}
                sx={{
                  borderTop: 1,
                  borderColor: 'divider',
                  mt: 1,
                  pt: 1.5
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Sign Out</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={handleMobileDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>

      {/* Logout confirmation modal */}
      <Dialog open={logoutConfirmOpen} onClose={handleLogoutCancel}>
        <DialogTitle>Sign Out</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to log out?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="inherit">
            No
          </Button>
          <Button onClick={handleLogoutConfirm} color="primary" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navigation;
